import React, { useState, useRef, useEffect } from 'react';

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  maxWords?: number;
}

export function EditableText({
  value,
  onChange,
  multiline = false,
  className = '',
  style = {},
  placeholder = 'Add text...',
  maxWords
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentValue(e.target.value);
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    let finalValue = currentValue;
    if (maxWords) {
      const words = finalValue.split(/\s+/).filter(Boolean);
      if (words.length > maxWords) {
        finalValue = words.slice(0, maxWords).join(' ') + '...';
      }
    }
    if (finalValue !== value) {
      onChange(finalValue);
    }
    setCurrentValue(finalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      setCurrentValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    const commonStyles: React.CSSProperties = {
      ...style,
      width: '100%',
      background: 'rgba(0,0,0,0.05)',
      border: '1px solid #facc15',
      borderRadius: '4px',
      padding: '4px',
      outline: 'none',
      color: style.color || 'inherit',
      fontFamily: style.fontFamily || 'inherit',
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      lineHeight: style.lineHeight,
      letterSpacing: style.letterSpacing,
      textTransform: style.textTransform as any,
    };

    if (multiline) {
      const wordsCount = currentValue.split(/\s+/).filter(Boolean).length;
      const isOverLimit = maxWords && wordsCount > maxWords;

      const handleAddBullet = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const lines = currentValue.split('\n');
        const bulleted = lines.map(line => {
          const trimmed = line.trim();
          if (!trimmed) return '';
          if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* ')) return trimmed;
          return `• ${trimmed}`;
        }).join('\n');
        setCurrentValue(bulleted);
      };

      return (
        <div style={{ position: 'relative', width: '100%', zIndex: 50 }}>
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={currentValue}
            onChange={handleInputChange}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onPointerDown={(e) => e.stopPropagation()}
            className={className}
            style={{ 
              ...commonStyles, 
              minHeight: '110px', 
              resize: 'vertical',
              borderColor: isOverLimit ? '#ef4444' : '#facc15'
            }}
            placeholder={placeholder}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <button 
                onPointerDown={(e) => { e.preventDefault(); handleSave(); }} 
                style={{ background: '#facc15', color: '#1c1917', border: 'none', padding: '2px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Done
              </button>
              <button
                type="button"
                onPointerDown={handleAddBullet}
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                title="Convert lines into bullet points"
              >
                <span style={{ fontSize: '1rem', lineHeight: 0.8 }}>•</span> Bullet Points
              </button>
            </div>
            {maxWords && (
              <span style={{ fontSize: '0.7rem', color: isOverLimit ? '#ef4444' : '#6b7280', fontWeight: 600 }}>
                {wordsCount} / {maxWords} words
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ position: 'relative', width: '100%', zIndex: 50 }}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          onPointerDown={(e) => e.stopPropagation()}
          className={className}
          style={{ ...commonStyles, width: '100%' }}
          placeholder={placeholder}
        />
        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', zIndex: 51 }}>
          <button 
            onPointerDown={(e) => { e.preventDefault(); handleSave(); }} 
            style={{ background: '#facc15', color: '#1c1917', border: 'none', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Render view mode with clean bullet alignment if text contains bullets or newlines
  const hasBullets = value && (value.includes('•') || value.includes('\n•') || /^\s*[-*]\s+/m.test(value));
  
  const renderFormattedContent = () => {
    if (!value) {
      return <span style={{ opacity: 0.5 }}>{placeholder}</span>;
    }

    if (multiline && hasBullets) {
      const lines = value.split('\n').filter(Boolean);
      return (
        <ul style={{ margin: 0, paddingLeft: '1.2em', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '0.35em' }}>
          {lines.map((line, idx) => {
            const cleanLine = line.replace(/^[•\-\*]\s*/, '').trim();
            if (!cleanLine) return null;
            return (
              <li key={idx} style={{ lineHeight: 1.5, paddingLeft: '0.2em' }}>
                {cleanLine}
              </li>
            );
          })}
        </ul>
      );
    }

    if (multiline && value.includes('\n')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4em' }}>
          {value.split('\n').map((paragraph, idx) => (
            paragraph.trim() ? <p key={idx} style={{ margin: 0, lineHeight: 1.6 }}>{paragraph}</p> : null
          ))}
        </div>
      );
    }

    return <span>{value}</span>;
  };

  return (
    <div
      onClick={() => setIsEditing(true)}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={className}
      style={{ ...style, position: 'relative', cursor: 'text', minHeight: '1em' }}
    >
      {renderFormattedContent()}
      {isHovered && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#facc15', color: '#1c1917', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
        </div>
      )}
      {maxWords && isHovered && (
        <div style={{ position: 'absolute', bottom: '-20px', right: '0', fontSize: '10px', color: '#facc15' }}>
          Max {maxWords} words
        </div>
      )}
    </div>
  );
}
